<template>
    <div>
        <MainModal :show-modal="showModal" size="sm" :footer="false" @close="$emit('close')">
            <template #body>
                <div class="mb--5 p-4">
                    <div class="flex align-items-center justify-content-center success__img__wrap">                        
                        <IconSvg name="layer" width="48px" height="48px"  />
                    </div>

                    <div class="mx-auto mt--20">
                        <div>
                            <p class="text-center text--xl text--semi--bold text--upper">
                                {{ bodyText.title }}
                            </p>

                            <p class="text-center text-gray-600 mt--10 text--sm">
                                {{ bodyText.desc }}
                            </p>

                            <p class="text-center text-gray-600 mt--10 text--sm">
                                {{ bodyText.secondDesc }}
                            </p>
                        </div>
                    </div>

                    <div v-if="scheduledState === 'show_schedule'" class="mt--30">
                        <FormLabel title="Pick a start date" />
                        <v-date-picker
                            v-model="form.startDate" class="text--sm" mode="dateTime" borderless
                            is24hr title-position="left"
                        >
                            <template #default="{ inputValue, inputEvents }">
                                <input
                                    class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                    :value="inputValue"
                                    nav-visibility="click"
                                    v-on="inputEvents"
                                >
                            </template>
                        </v-date-picker>
                    </div>

                    <div class="flex justify-content-center mt--20">
                        <BaseButton 
                            label="Schedule start date" 
                            prefix-icon 
                            variant-style="primary"
                            icon-name="calendar"
                            :loading="scheduledState === 'start_schedule'" 
                            :disabled="scheduleProjectDisableBtn" 
                            @submit="scheduleProjectStart"
                        />
                    </div>
                </div>

                <div class="footer-modal w-100 mx-auto">
                    <BaseButton 
                        class="mr--5" 
                        label="Start project" 
                        variant="alt" 
                        prefix-icon 
                        variant-style="dark"
                        icon-name="play-circle"
                        :loading="startProjectLoading" 
                        @submit="handleStartProject"
                    />
                    <BaseButton 
                        type="submit"
                        label="Skip and close"
                        variant="ghost"
                        class="mt--15 p--0"
                        @submit="$emit('close')"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import BaseButton from "../buttons/BaseButton.vue";
import FormLabel from "../forms/FormLabel.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import MainModal from "./MainModal.vue";

export default {
    name: "ProgressSuccess",

    components: {
        MainModal,
        IconSvg,
        BaseButton,
        FormLabel,
    },

    props: {
        showModal: {
            type: Boolean,
            default: false,
        },
        projectId: { type: String, default: "" },
    },

    data() {
        return {
            scheduledState: "not_scheduled",
            form: {
                startDate: null,
            },
            loading: false,
            startProjectLoading: false,
        };
    },

    computed: {
        scheduleProjectDisableBtn() {
            if (this.scheduledState === "show_schedule") {
                return !this.form.startDate;
            }
            return false;
        },

        bodyText() {
            switch (this.scheduledState) {
            case "show_schedule":
            case "not_scheduled":
                return {
                    title: "Start project?",
                    desc: "Automatically start a time tracker and update the project status to \"In progress\".",
                    secondDesc: "Want to start this project at a later time? Click on the \"Schedule later\" button below",
                };
            
            case "start_schedule":
                return {
                    title: "Scheduling...",
                    desc: "",
                    secondDesc: "",
                };
        
            case "scheduled":
                return {
                    title: "Project successfully scheduled!",
                    desc: " Closing this popup and redirecting to project details page...",
                    secondDesc: "",
                };
            
            default:
                return {
                    title: "Start project?",
                    desc: "Sets a time tracker and updates project status to \"In progress\".",
                    secondDesc: "Want to start this project at a later time instead? Click on the button below",
                };
            }
        },
    },

    methods: {
        async scheduleProjectStart() {
            if (this.scheduledState === "not_scheduled") {
                this.scheduledState = "show_schedule";
                return;
            }

            try {
                this.scheduledState = "start_schedule";
                
                const payload = {
                    trackingType: "scheduled",
                    startDate: this.form.startDate,
                    trackProject: true,
                };

                await this.$store.dispatch("project/scheduleProject", { payload, id: this.projectId });
                this.scheduledState = "scheduled";
                
                this.form.startDate = null;
                this.$emit("close");
                return this.$router.push({ name:"project-details-view", params: { id: this.projectId }});
            } catch (error) {
                this.scheduledState = "show_schedule";
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },

        async handleStartProject() {
            try {
                this.startProjectLoading = true;

                const payload = { 
                    projectData: {
                        trackingType: "automatic",
                        status: "in progress",
                        trackProject: true,
                        startDate: Date.now(),
                    },
                    logData: {
                        eventType: "start",
                        startTime: Date.now(),
                        endTime: null,
                        projectId: this.projectId,
                    },
                };

                await this.$store.dispatch("project/startProject", { payload, id: this.projectId });
                this.$emit("close");
                
                return this.$router.push({ name:"project-details-view", params: { id: this.projectId }});
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.startProjectLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.success__img__wrap {
    position: relative;
}

.footer-modal {
    font-size: 18px;
    font-weight: 500; 
    border-top: 1px solid #eee;
    padding: 12px 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 0px;
}
</style>